import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { applySEO } from '@/components/feature/SEOHead';
import { chapters, featuredNovel } from '@/mocks/novelData';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [fontSize, setFontSize] = useState('text-base');

  const chapter = chapters.find((ch) => ch.id === id);
  const currentIndex = chapters.findIndex((ch) => ch.id === id);
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;

  useEffect(() => {
    if (chapter) {
      applySEO({
        title: `${chapter.title} - ${featuredNovel.title}最新章节 | 笔趣阁`,
        description: `${featuredNovel.title}最新章节${chapter.title}免费阅读，笔趣阁同步更新。作者${featuredNovel.author}，全文无删减，支持手机电脑多端阅读。更新时间：${chapter.updateTime}`,
        keywords: `${featuredNovel.title},${chapter.title},${featuredNovel.author},笔趣阁最新章节,${featuredNovel.title}笔趣阁,免费阅读`,
        canonicalUrl: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') + '/article/' + id : '',
        ogType: 'article',
        ogImage: featuredNovel.coverImage,
        schema: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: chapter?.title || '',
          description: `${featuredNovel.title}最新章节${chapter?.title || ''}，作者${featuredNovel.author}。`,
          author: {
            '@type': 'Person',
            name: featuredNovel.author,
          },
          datePublished: chapter?.updateTime || '',
          dateModified: chapter?.updateTime || '',
          wordCount: chapter?.wordCount || 0,
          isPartOf: {
            '@type': 'Book',
            name: featuredNovel.title,
            author: {
              '@type': 'Person',
              name: featuredNovel.author,
            },
          },
        },
      });
    }
  }, [chapter, id]);

  if (!chapter) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-warning-line text-foreground-400 text-3xl"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">章节未找到</h2>
            <p className="text-foreground-500 mb-6">该章节可能已被移除或链接失效</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const paragraphs = chapter.content.split('\n').filter((p) => p.trim());

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        {/* Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: featuredNovel.title, href: '/' },
                { label: chapter.title },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {chapter.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {featuredNovel.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {chapter.updateTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-word-line"></i>
                  {chapter.wordCount.toLocaleString()} 字
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Reading Controls */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFontSize('text-sm')}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium transition-colors cursor-pointer ${
                  fontSize === 'text-sm' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                小
              </button>
              <button
                onClick={() => setFontSize('text-base')}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  fontSize === 'text-base' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                中
              </button>
              <button
                onClick={() => setFontSize('text-lg')}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-base font-medium transition-colors cursor-pointer ${
                  fontSize === 'text-lg' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                大
              </button>
            </div>
            <div className="flex items-center gap-2">
              {prevChapter && (
                <Link
                  to={`/article/${prevChapter.id}`}
                  className="px-4 py-2 text-sm text-foreground-700 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-arrow-left-line text-xs"></i> 上一章
                </Link>
              )}
              <Link
                to="/"
                className="px-4 py-2 text-sm text-foreground-700 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                目录
              </Link>
              {nextChapter && (
                <Link
                  to={`/article/${nextChapter.id}`}
                  className="px-4 py-2 text-sm text-foreground-700 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
                >
                  下一章 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className={`${fontSize} leading-relaxed text-foreground-800 space-y-4`}>
              {paragraphs.map((para, idx) => {
                const isDivider = para.trim() === '——';
                const isMeta = para.startsWith('更新时间') || para.startsWith('下一章预告');
                if (isDivider) {
                  return (
                    <div key={idx} className="text-center py-2">
                      <span className="text-foreground-300">————</span>
                    </div>
                  );
                }
                if (isMeta) {
                  return (
                    <p key={idx} className="text-sm text-foreground-400 italic">
                      {para}
                    </p>
                  );
                }
                if (para === '（未完待续）') {
                  return (
                    <p key={idx} className="text-center font-heading text-foreground-500 py-4">
                      {para}
                    </p>
                  );
                }
                return (
                  <p key={idx} className="indent-8">
                    {para}
                  </p>
                );
              })}
            </div>

            {/* Bottom Navigation */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-4">
                {prevChapter ? (
                  <Link
                    to={`/article/${prevChapter.id}`}
                    className="flex items-center gap-2 px-5 py-3 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer group"
                  >
                    <i className="ri-arrow-left-line text-foreground-500 group-hover:text-primary-500 transition-colors"></i>
                    <div className="text-left">
                      <p className="text-xs text-foreground-400">上一章</p>
                      <p className="text-sm text-foreground-700 font-medium truncate max-w-[200px]">{prevChapter.title}</p>
                    </div>
                  </Link>
                ) : (
                  <div></div>
                )}
                {nextChapter ? (
                  <Link
                    to={`/article/${nextChapter.id}`}
                    className="flex items-center gap-2 px-5 py-3 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer group"
                  >
                    <div className="text-right">
                      <p className="text-xs text-background-200">下一章</p>
                      <p className="text-sm text-background-50 font-medium truncate max-w-[200px]">{nextChapter.title}</p>
                    </div>
                    <i className="ri-arrow-right-line text-background-50"></i>
                  </Link>
                ) : (
                  <div></div>
                )}
              </div>
            </div>

            {/* Chapter List Quick Nav */}
            <div className="mt-8 p-5 bg-background-100 rounded-lg">
              <h3 className="font-heading font-semibold text-foreground-900 mb-3">最新章节列表</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {chapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/article/${ch.id}`}
                    className={`px-3 py-2 rounded-md text-sm transition-colors cursor-pointer truncate ${
                      ch.id === id
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {ch.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}